/*
 *   File : request-history.js
 *   Author : https://evoqins.com
 *   Description : Request history page
 *   Integrations : NA
 *   Version : 1.0.0
*/

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Custom components 
import { DataTableComponent } from '../../../Component/DataTable';
import { CustomSearchInput } from '../../../Component/FormElements';
import { APIService } from '../../../Service';
import { Loader } from '../../../Component/Others';
import { EmptyScreen } from '../../../Component/EmptyComponent';
import { DownloadButton } from '../../../Component/Buttons';
import { toast } from 'react-toastify';

const PAGE_SORT_FIELDS = [
    'id',
    'name',
    'email',
    'phone',
    'store_id',
    'store_name',
    'created',
    'updated',
    'dob',
    'status',
];

const RequestHistory = () => {

    const navigate = useNavigate();
    const isSortingRef = useRef(false);
    const skipListFetchRef = useRef(false);

    const [searchValue, setSearchValue] = useState("");
    const [historyData, setHistoryData] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);

    const [downloadLoader, setDownloadLoader] = useState(false);

    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    useEffect(() => {
        window.scrollTo({
            top: 0, behavior: 'smooth' //scroll top
        });
    }, []);

    useEffect(() => {
        if (skipListFetchRef.current) {
            skipListFetchRef.current = false;
            return;
        }
        _getHistoryData();
    }, [searchValue, currentPage]);

    // Request history table columns
    const TABLE_COLUMNS = [
        {
            name: 'Name',
            id: 'name',
            sortField: 'name',
            selector: row => row.name,
            width: "190px",
            sortable: true
        },
        // {
        //     name: 'Email',
        //     id: 'email',
        //     sortField: 'email',
        //     selector: row => row.email !== null ? row.email : '-',
        //     width: "200px",
        //     sortable: true
        // },
        {
            name: 'Phone no.',
            id: 'phone',
            sortField: 'phone',
            // selector: row => row.phone,
            cell: row =>
                <a href={`tel:${row.phone}`} className='text-decoration-none text-decoration-hover-underline e-text-rich-black' >
                    {row.phone}
                </a>,
            width: "160px",
            sortable: true
        },
        {
            name: 'Dob',
            id: 'dob',
            sortField: 'dob',
            selector: row => row.dob,
            cell: row => <>{row.dob}</>,
            format: row => row.dob,
            width: "180px",
            sortable: true
        },
        {
            name: 'Store',
            id: 'store_name',
            sortField: 'store_name',
            selector: row => row.store_name,
            width: "260px",
            sortable: true
        },
        {
            name: 'Status',
            id: 'status',
            sortField: 'status',
            // selector: row => row.status,
            cell: row =>
                <>
                    {row.status ?
                        <div className={`${row.status === 1 ? `e-bg-tea-green padding-12px-lr` :
                            row.status === 2 ? `e-bg-light-red padding-12px-lr` : ``} py-1 e-border-radius-16 e-font-weight-500 e-font-12`}>
                            {row.status === 1 ? "Approved" : "Rejected"}
                        </div>
                        :
                        "--"
                    }
                </>,
            width: "120px",
            sortable: true
        },
        {
            name: 'Country admin',
            selector: row => "-",
            width: "180px",
        },
        {
            name: 'Status updated on',
            id: 'updated',
            sortField: 'updated',
            selector: row => row.updated,
            width: "180px",
            sortable: true,
            format: row => row.updated,

            // sortFunction: (a, b) => {
            //     let first_date = new Date(a.updated);
            //     let second_date = new Date(b.updated);
            //     return first_date - second_date;
            // }
        }
    ];

    // handle page change 
    const _handlePageChange = (page_num) => {
        if (isSortingRef.current) {
            isSortingRef.current = false;
            return;
        }

        if (page_num === currentPage) {
            return;
        }

        setCurrentPage(page_num);

        window.scrollTo({
            top: 0, behavior: 'smooth'
        });
    };

    const _handleSort = (column, direction) => {
        const sortBy = column.sortField || column.id || null;

        if (!sortBy || !PAGE_SORT_FIELDS.includes(sortBy)) {
            return;
        }

        isSortingRef.current = true;
        setSortField(sortBy);
        setSortDirection(direction);
        setLoading(true);

        // Only skip the list useEffect when page will actually change.
        if (currentPage !== 1) {
            skipListFetchRef.current = true;
        }
        setCurrentPage(1);
        _getHistoryData(1, sortBy, direction);

        setTimeout(() => {
            isSortingRef.current = false;
        }, 0);
    };

    // handle clear searched value 
    const _handleClearSearchValue = () => {
        setSearchValue("");
        setLoading(true);
        setSortField(null);
        setSortDirection(null);
    }

    // API - get registration history 
    const _getHistoryData = (page_num = currentPage, sort_by = sortField, sort_order = sortDirection) => {
        const effective_sort_by = sort_by && PAGE_SORT_FIELDS.includes(sort_by) ? sort_by : null;
        const effective_sort_order = effective_sort_by ? sort_order : null;

        let is_post = true;
        let url = 'CA/registration/history';
        let data = {
            "page_num": page_num,
            "query": searchValue,
            "sort_by": effective_sort_by ? [effective_sort_by] : null,
            "order": effective_sort_order ? [effective_sort_order] : null,
        };
        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setHistoryData(response.data)
            } else {
                setHistoryData({ data: [] })
            }
            setLoading(false);
        });
    };

    // API - Download data
    const _download = () => {
        setDownloadLoader(true);

        let is_post = false;
        let url = "CA/registration/download";
        let data = {};

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                const url = response.file_url;
                const link = document.createElement('a');
                link.href = url;
                link.download = '';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setDownloadLoader(false);
        });
    }


    return (
        <section className='pb-5 '>
            <div className='e-bg-lavender-blush-light padding-12px-top padding-98px-bottom padding-52px-lr'>
                <div className='d-flex gap-0-8 align-items-center'>
                    <img src={require("../../../Assets/Image/left-back-arrow.svg").default}
                        draggable={false}
                        className='cursor-pointer'
                        alt='arrow'
                        onClick={() => navigate("/manage-request")} />
                    <h1 className='mb-0 e-primary-font-playfair e-font-24 line-height-32px e-text-rich-black e-font-weight-600'>
                        Request RSVP history
                    </h1>
                </div>
            </div>
            <div className='margin-neg-66px-top padding-52px-lr'>
                <div className='e-bg-white e-admin-box-shadow py-4 e-border-radius-16 '>
                    <div className='row px-4'>
                        <div className='col-6'>
                            <h4 className='mb-0 e-text-rich-black e-alt-font-noto-sans e-font-20 e-font-weight-600 line-height-28px' >
                                Requests
                            </h4>
                        </div>
                        <div className='col-6 text-end'>
                            <div className='d-flex align-items-center gap-4 justify-content-end'>

                                <DownloadButton loader={downloadLoader}
                                    onClick={_download} />

                                {/* search input */}
                                <div className='position-relative'>
                                    <CustomSearchInput placeholder="Search by name, id & store"
                                        type="text"
                                        require
                                        value={searchValue}
                                        className="w-100 padding-10px-tb"
                                        labelStyle="e-font-weight-500"
                                        handleChange={(e) => {
                                            setSearchValue(e);
                                            setCurrentPage(1);
                                        }}
                                        handleClear={() => _handleClearSearchValue()} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {loading ?
                        // loader 
                        <Loader />
                        :
                        <>
                            {/* request history data table */}
                            {historyData.data.length !== 0 ?
                                <div className='e-table'>
                                    <DataTableComponent
                                        key={`request-history-${currentPage}-${sortField || 'none'}-${sortDirection || 'none'}`}
                                        columns={TABLE_COLUMNS}
                                        data={historyData.data}
                                        pagination={true}
                                        paginationServer={true}
                                        paginationTotalRows={historyData.total_records}
                                        paginationDefaultPage={currentPage}
                                        paginationResetDefaultPage={false}
                                        sortServer={true}
                                        defaultSortFieldId={sortField}
                                        defaultSortAsc={sortDirection !== 'desc'}
                                        onSort={_handleSort}
                                        onChangePage={_handlePageChange} />
                                </div>
                                :
                                <div className='h-100vh pt-5 mt-5'>
                                    <EmptyScreen message="No requests found" />
                                </div>
                            }
                        </>
                    }
                </div>
            </div>
        </section>
    )
}

export default RequestHistory;
